/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { v4 as uuidv4 } from 'uuid';
import { ITab } from '../../api/workspace/types';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getAllWorkspacesThunk, getWorkspaceByIdThunk, savePlotOptionsThunk, T_addWorkspace, T_editWorkspace, T_GetUserActions, T_getProjectView, T_editWorkspaceFilters, T_editWorkspaceFilterOptions, saveMapOptionsThunk, T_getStyleSettings, T_changeStyleSettings, T_getAxisStyleSettings } from './thunk';
import { workspaceState, IWorkspaceItem, IPlot, IWorkspace, IFilterLegend, IProjectViewOption, MapOptions, IFilterOptions, IStyleSettings, IAxisStyleSettings, ICategorizeOptions } from './types';
import { IFilter, IOptions, ITagAttribute } from '../sidebar/types';
import { ICategorizeOption, TreeViewDataItem } from 'store/sidebar/types';
import { getSideBarFiltersThunk, GetTagAttributeAllowedValuesThunk, GetDropdownAllTagAttributeAllowedValuesThunk, GetTagAttributeInfoThunk } from 'store/sidebar/thunk';
import { checkFilterActiveStatus } from 'helpers/functions/sidebar';
import { RootState } from '../store';
import { saveWorkSpaceToLS, getWorkspaceViewStates, saveWorkspaceViewState } from 'helpers/functions/workspaceHelpers';
import { IUserActions } from 'api/userAction/types';
import { deepClone, normalizeByKey } from 'helpers/functions/commons';
import { setSharedUsersThunk } from 'store/home/thunk';

function getNestedValueClone(tree: RootState['workspaceSlice']['tree'] | RootState['workspaceSlice']['filteredFilters'], keyString: string) {
    const indexes = keyString.split('_');
    const keyStringWithItems = indexes.join('_items_')
    const keys = keyStringWithItems.split('_')
    return keys.reduce((nestedTree: any, key: any) => {
        if (nestedTree && typeof nestedTree === 'object' && key in nestedTree) {
            return nestedTree[key];
        } else {
            return undefined;
        }
    }, deepClone(tree));
}

function getNestedValue(tree: RootState['workspaceSlice']['tree'] | RootState['workspaceSlice']['filteredFilters'], keyString: string) {
    const indexes = keyString.split('_');
    const keyStringWithItems = indexes.join('_items_')
    const keys = keyStringWithItems.split('_')
    return keys.reduce((nestedTree: any, key: any) => {
        if (nestedTree && typeof nestedTree === 'object' && key in nestedTree) {
            return nestedTree[key];
        } else {
            return undefined;
        }
    }, tree[0].items);
}

const getItemByHierarchicalIndex = (itemHierarchicalIndex: 'string', state: RootState['workspaceSlice']['tree'] | RootState['workspaceSlice']['filteredFilters']) => {
    const value = getNestedValueClone(state[0].items, itemHierarchicalIndex)
    return value
}

export const initialStyleSettings: IStyleSettings = {
    color: "rgba(0, 0, 0, 1)",
    font: { name: "Arial", family: 'Arial' },
    shape: { id: "Circle", url: `${window.location.origin}/circle.png` },
    sizeFont: { name: 'Small', value: '12px' },
    sizeShape: 'x1',
    position: { label: 'Right', value: 'start' },
    border: true,
    optionId: '',
    transparent: 0.2
}

export const initialAxisStyleSettings: IAxisStyleSettings = {
    title: {
        font: { name: "Arial", family: 'Arial' },
        sizeFont: { name: 'Medium', value: '16px' },
    },
    x: {
        font: { name: "Arial", family: 'Arial' },
        sizeFont: { name: 'Small', value: '12px' },
    },
    y: {
        font: { name: "Arial", family: 'Arial' },
        sizeFont: { name: 'Small', value: '12px' },
    },
    y2: {
        font: { name: "Arial", family: 'Arial' },
        sizeFont: { name: 'Small', value: '12px' },
    }

}

const clearFilter = (items: TreeViewDataItem[]) => {
    if (!items.length) return
    items.forEach(filter => {
        if (!('filterApplied' in filter)) return clearFilter(filter.items)
        filter.filterApplied = false
    })
}

const initialState: workspaceState = {
    tree: [],
    hiddenSidebar: true,
    restrictionPopup: {
        open: false,
        message: ''
    },
    filtersSearchText: '',
    filtersTreeView: [
        {
            name: 'Filters',
            element: 'filters',
            type: 'title',
            category: 'title',
            expanded: false,
            id: uuidv4(),
            items: [],
        },
    ],
    loadAllowedValues: false,
    projectViewTreeView: {
        name: 'Project view options',
        element: 'projectViewOptions',
        category: 'title',
        type: 'title',
        expanded: true,
        id: uuidv4(),
        items: [
            {
                category: 'projectView', element: 'projectViewoption', type: 'select',
                options: [],
                selected: { name: '', value: '', id: uuidv4(), type: '' },
                id: uuidv4(),
                items: []
            },
        ]
    },
    plots: [],
    categoriseOptions: [],
    stringOptions: [],
    numericOptions: [],
    projectViewOptions: [],
    mapOptions: {
        mapType: 0,
        showUserData: false,
        showGlobalDatabase: false,
        showFilters: true,
        showUserDataLegends: true,
        subsurfaceAssets: false,
        ccsProjects: false,
        cO2Emitters: false,
        globalPipelineNetworks: false,
        blocks: false,
        licences: false,
        windfarms: false,
        tooltips: [],
        transparent: 0.2,
    },
    workspace: null,
    workspaceId: '',
    plotId: '',
    workspaces: [],
    isWorkspaceListFetched: false,
    workspaceListLoading: false,
    workspaceEdited: false,
    activeFiltersLegend: [],
    deletePlot: false,
    tagAttributeAllowedValues: [],
    dropdownAllTagAttributeValues:[],
    userActions: {
        workspaceId: '',
        action: 0,
        entityId: '',
        actionName: ''
    },
    filters: {
        id: '',
        title: '',
        type: 1,
        xTagAttributeId: '',
        yTagAttributeId: '',
        quickPlotId: '',
        plotOptions: {
            axisOptions: {
                xMin: null,
                xMax: null,
                yMin: null,
                yMax: null,
                xGridlines: true,
                yGridlines: true,
                xLogarithmic: false,
                yLogarithmic: false,
                xInvert: false,
                yInvert: false
            },
            regressions: {
                addPoint: false,
                expRegression: false,
                label: '',
                linRegression: false,
                logRegression: false,
                powRegression: false,
                xLogLines: false,
                xValue: null,
                yLogLines: false,
                zeroIntercept: false,
                zeroInterceptValue: 0,
            },
            legends: {
                showFilters: true,
                showUserData: true,
                showRegressionEquations: true
            },
            labels: {
                showUserDataLabels: false,
                globalDatabaseLabels: false,
                showAxisLabels: true
            },
            tooltips: []
        },
        categorizeOptions: {
            tagAttributeId: null,
            enabled: false,
            logScale: false,
            applyToUserData: false,
            mode: 'basic'
        },
        filterOptions: {
            applyToUserData: false,
            applyToBenchmarkData: false,
            isEnabled: true,
            applyToBenchmarkPlots:false,
            filters: []
        },
        labelTemplateHover: '</br><b>#value</b></br>',
        categoryTemplateHover: '<b>#key:</b> #value</br>',
        tooltipTemplateHover: '<b>#key:</b> #value</br>',
        extraTemplateHover: '<extra></extra>',
    },
    filteredFilters: [],
    tabs: [],
    isLoading: false,
    error: null,
    styleSettings: {
        color: "rgba(255, 140, 0, 1)",
        font: { name: "Arial", family: 'Arial' },
        shape: { id: "Circle", url: `${window.location.origin}/circle.png` },
        sizeFont: { name: 'Small', value: '12px' },
        sizeShape: 'x1',
        position: { label: 'Right', value: 'start' },
        border: true,
        optionId: '',
        transparent: 0.99
    },
    axisStyleSettings: initialAxisStyleSettings,
    workspaceViewStates: getWorkspaceViewStates()
};

export const workspaceSlice = createSlice({
  name: "workspace",
  initialState,
  reducers: {
    setFilterOptionItem: (state, action) => {
      const { element, value } = action.payload

      if (element === "isEnabled") state.tree[0].name = state.filters.filterOptions.filters.length && state.filters.filterOptions.isEnabled ? "Filters (DISABLED)" : "Filters"

      state.filters.filterOptions[element as keyof IFilterOptions] = value
    },
    setFiltersSearchText: (state, action) => {
      state.filtersSearchText = action.payload
    },
    setStyleSettings: (state, action: PayloadAction<IStyleSettings>) => {
      state.styleSettings = action.payload
    },
    setAxisStyleSettings: (state, action: PayloadAction<Partial<IAxisStyleSettings>>) => {
      state.axisStyleSettings = { ...state.axisStyleSettings, ...action.payload }
    },
    setTagAttributeAllowedValues: (state, action) => {
      state.tagAttributeAllowedValues = action.payload;
    },
    setMapOptionItem: (state, action) => {
      const { element, value, index } = action.payload

      if (element.includes("tooltip")) {
        if (index > -1) {
          // Handle case when value is null (when selection is cleared)
          if (value === null) {
            // Set the tooltip at this index to null when cleared
            if (state.mapOptions.tooltips && Array.isArray(state.mapOptions.tooltips)) {
              state.mapOptions.tooltips[index] = { id: '', tagAttributeId: '', name: '', text: '' };
            }
          } else if (!state.mapOptions.tooltips[index]) {
            state.mapOptions.tooltips = new Array(5).fill(null).map((item, i) => (i === index ? value : state.mapOptions.tooltips[i] || null))
          } else {
            // Ensure value has id/tagAttributeId and text properties before accessing them
            if (value && typeof value === 'object') {
              // Use either id or tagAttributeId, whichever is available
              state.mapOptions.tooltips[index].tagAttributeId = value.id || value.tagAttributeId || null;
              state.mapOptions.tooltips[index].text = value.text || '';
            }
          }
        }
      } else {
        state.mapOptions[element as keyof MapOptions] = value as never
      }
    },

    handleRestrictionPopup: (state, action) => {
      state.restrictionPopup.open = action.payload.open
      state.restrictionPopup.message = action.payload.message
    },

    changePlotTitle: (state, action) => {
      const selectedTab = state.tabs.find((e: ITab) => e.id === action.payload.id)
      if (selectedTab) selectedTab.title = action.payload.value
    },

    setTree: (state, action) => {
      state.tree = action.payload
      state.filtersTreeView = action.payload
    },

    setFilteredFilters: (state, action) => {
      state.filteredFilters = action.payload
    },

    hideSidebar: (state, action) => {
      state.hiddenSidebar = action.payload
    },

    setDeletePlot: (state, action) => {
      state.deletePlot = action.payload
    },

    showPlotOptions: (state) => {
      state.tree = state.tree.filter((item) => item.element === "filterOptions" || item.element === "filters")
    },

    hidePlotOptions: (state) => {
      state.tree = state.tree.filter((item) => item.element !== "plotOptions")
    },

    hideMapOptions: (state) => {
      state.tree = state.tree.filter((item) => item.element !== "mapOptions")
    },

    showProjectViewOptions: (state) => {
      state.tree = state.tree.filter((item) => item.element === "filterOptions" || item.element === "filters")
      if (state.tree.every((item) => item.element !== "projectViewOptions")) {
        state.tree.push(state.projectViewTreeView)
      }
    },

    hideProjectViewOptions: (state) => {
      state.tree = state.tree.filter((item) => item.element !== "projectViewOptions")
    },

    setAxis: (state, action) => {
      const { x, y, xAxis, yAxis } = action.payload
      const currentX = state.tree[2].items.find((item: TreeViewDataItem) => item.name === "X Axis")
      const currentY = state.tree[2].items.find((item: TreeViewDataItem) => item.name === "Y Axis")
      if (currentX) currentX.selected = { name: xAxis, id: x, type: "" }
      if (currentY) currentY.selected = { name: yAxis, id: y, type: "" }
    },

    checkItem: (state, action) => {
      const {
        props: { itemHierarchicalIndex },
        value,
      } = action.payload
      
      // Get the hierarchical parts
      const indexes = itemHierarchicalIndex.split("_");
      const keyStringWithItems = indexes.join("_items_");
      const keys = keyStringWithItems.split("_");
      
      // Create a deep clone of the item
      const current = getItemByHierarchicalIndex(itemHierarchicalIndex, state.tree);
      
      // Create a new object with updated checked property
      const updatedItem = {
        ...current,
        checked: value,
        filterApplied: checkFilterActiveStatus({...current, checked: value})
      };
      
      // Update the item in the tree using the keys
      keys.reduce(
        (nestedTree: any, key: string, index: number) => {
          if (nestedTree && typeof nestedTree === "object" && key in nestedTree) {
            if (index === keys.length - 1) {
              nestedTree[key] = updatedItem;
            }
            return nestedTree[key];
          } else {
            return undefined;
          }
        },
        state.tree,
      );
    },

    setQuickPlotId: (state, action) => {
      state.tabs.find((tab: ITab) => {
        if (tab.isSelected) tab.quickPlotId = action.payload
      })
    },

    selectItem: (state, action) => {
      state.tree.find((product) => {
        if (product.items) {
          const current = product.items.find((item) => item.id === action.payload.props.item.id)
          if (current) {
            current.selected = action.payload.value
            current.filterApplied = checkFilterActiveStatus(current)
          }
        }
      })
    },

    changeInputValue: (state, action) => {
      const { itemHierarchicalIndex, value } = action.payload
      const item = getItemByHierarchicalIndex(itemHierarchicalIndex, state.tree)
      item.inputValue = value
      item.filterApplied = checkFilterActiveStatus(item)
    },

    changePlotOptions: (state, action) => {
      const { name, value } = action.payload
      const filters = state.filters
      const options = filters.plotOptions

      if (name === "plotTitle") {
        filters.title = value
      }

      if (name === "plotType") {
        const type = state.tree[2].items.find((i) => i.element === "plotType")
        if (type) type.selected = value
        filters.type = value.name === "Histogram" ? 2 : value.name === "Statistics" ? 3 : 1
      }

      if (name.includes("Axis")) {
        name === "xAxis" ? (filters.xTagAttributeId = value) : (filters.yTagAttributeId = value)
      }

      if (name === "tooltip") {
        options?.tooltips?.push(value.id)
      }

      const plotCategories = Object.keys(options ?? {})

      plotCategories.map((category) => {
        const field = category as keyof typeof options
        const optionField = options?.[field] || {}
        if (name in optionField) {
          ;(optionField[name as keyof typeof optionField] as string) = value
        }
      })
    },

    changeNumberInputValue: (state, action) => {
      const {
        props: { itemHierarchicalIndex },
        filtersSearched,
      } = action.payload
      const item = itemHierarchicalIndex !== "" ? (!filtersSearched ? getItemByHierarchicalIndex(itemHierarchicalIndex, state.tree) : getItemByHierarchicalIndex(itemHierarchicalIndex, state.filteredFilters)): {...action.payload.props.item};
      if (action.payload.input === "firstConditionInput") item.firstConditionInput = action.payload.value
      else if (action.payload.input === "secondConditionInput") item.secondConditionInput = action.payload.value
    },

    setCategoriseTagAttributeId: (state, action) => {
      if (!state.filters.categorizeOptions) {
        state.filters.categorizeOptions = { tagAttributeId: null, enabled: false, logScale: false, applyToUserData: false, mode: 'basic' };
      }
      state.filters.categorizeOptions.tagAttributeId = action.payload
    },

    setCategoriseEnabled: (state, action) => {
      if (!state.filters.categorizeOptions) {
        state.filters.categorizeOptions = { tagAttributeId: null, enabled: false, logScale: false, applyToUserData: false, mode: 'basic' };
      }
      state.filters.categorizeOptions.enabled = action.payload
    },

    setCategoriseLogScale: (state, action) => {
      if (!state.filters.categorizeOptions) {
        state.filters.categorizeOptions = { tagAttributeId: null, enabled: false, logScale: false, applyToUserData: false, mode: 'basic' };
      }
      state.filters.categorizeOptions.logScale = action.payload
    },

    setCategoriseUserData: (state, action) => {
      if (!state.filters.categorizeOptions) {
        state.filters.categorizeOptions = { tagAttributeId: null, enabled: false, logScale: false, applyToUserData: false, mode: 'basic' };
      }
      state.filters.categorizeOptions.applyToUserData = action.payload
    },

    setCategoriseMode: (state, action: PayloadAction<'basic' | 'advanced'>) => {
      if (!state.filters.categorizeOptions) {
        state.filters.categorizeOptions = { tagAttributeId: null, enabled: false, logScale: false, applyToUserData: false, mode: 'basic' };
      }
      state.filters.categorizeOptions.mode = action.payload
    },

    clearAllFilters: (state) => {
      state.activeFiltersLegend = []
      state.filters.filterOptions.filters = []
      state.tree[0].name = "Filters"
    },

    enableDisableFilters: (state) => {
      state.tree[0].name = state.filters.filterOptions.filters.length && state.filters.filterOptions.isEnabled ? "Filters (DISABLED)" : "Filters"
      state.filters.filterOptions.isEnabled = !state.filters.filterOptions.isEnabled
    },

    switchApplyToUserData: (state) => {
      state.filters.filterOptions.applyToUserData = !state.filters.filterOptions.applyToUserData
    },

    applyFilter: (state, action) => {
      try {
        const { firstConditionInput, firstConditionList, logicalOperationList, secondConditionInput, secondConditionList, name, id } = action.payload.props.item.item;
        // Get values directly from the item to ensure they're up-to-date
        const values = action.payload.props.item.item.values || [];
        
        const LegendValueFromConditionInputs = [
          { list: firstConditionList, input: firstConditionInput },
          { list: secondConditionList, input: secondConditionInput },
        ].reduce((acc, item) => {
          if (item.input) {
            acc += `${acc ? ` ${logicalOperationList.name}` : ""} ${item.list.name} ${item.input}`
          }
          return acc
        }, "")
        const currentActiveFiltersLegend = state.activeFiltersLegend.find((filterLegend: IFilterLegend) => filterLegend.filter === name)
        const finalValues: string[] = []
        if (values && values.length) {
          finalValues.push(...values) 
        }
        if (LegendValueFromConditionInputs) {
          finalValues.push(LegendValueFromConditionInputs)
        }

        if (currentActiveFiltersLegend) {
          currentActiveFiltersLegend.values = finalValues
        } else {
          state.activeFiltersLegend.push({
            filter: name,
            values: finalValues,
          })
        }

        const { filters } = state.filters.filterOptions

        const index = filters.findIndex((fil: IFilter) => fil.tagAttributeId === id)
        // try !checkFilterActiveStatus(action.payload.props.item)
        const {
          filtersSearched,
        } = action.payload;
        const { itemHierarchicalIndex } = action.payload.props.item;
        const item = itemHierarchicalIndex !== "" ? (!filtersSearched ? getNestedValue(state.tree, itemHierarchicalIndex) : getNestedValue(state.filteredFilters, itemHierarchicalIndex)) : {...action.payload.props.item.item};
        item.filterApplied = checkFilterActiveStatus(item, { firstConditionInput, secondConditionInput })

        item.firstConditionInput = firstConditionInput
        item.secondConditionInput = secondConditionInput
        item.firstConditionList = firstConditionList
        item.secondConditionList = secondConditionList
        item.logicalOperationList = logicalOperationList
        item.values = values

        if (values.length === 0 && !firstConditionInput && !secondConditionInput) {
          // remove filter if there is no values and filter words
          if (index > -1) {
            filters.splice(index, 1)
          }
          return
        }
        const filterModel: IFilter = {
          tagAttributeId: id,
          partOne: {
            value: firstConditionInput,
            ignoreCase: true,
            operationId: firstConditionList.id,
            operationNameNumeric: firstConditionList.name,
            operationNameString: firstConditionList.name,
          },
          booleanOperationId: logicalOperationList.id,
          partTwo: {
            value: secondConditionInput,
            ignoreCase: true,
            operationId: secondConditionList.id,
            operationNameNumeric: secondConditionList.name,
            operationNameString: secondConditionList.name,
          },
          values,
        }

        // update filter if it exists or add new filter
        if (index > -1) {
          filters[index] = filterModel
        } else {
          filters.push(filterModel)
        }
        
        if (!filters.length) {
          state.tree[0].name = "Filters"
        }
      } catch (error) {
        console.log(error)
      }
    },

    clearFiltersApplied: (state) => {
      clearFilter(state.tree)
      if (!state.filters.filterOptions.filters.length) {
        state.tree[0].name = "Filters"
      }
    },

    checkFilter: (state, action) => {
      const { filtersSearched } = action.payload
      const { itemHierarchicalIndex } = action.payload.props;
      const item = itemHierarchicalIndex != '' ? (!filtersSearched ? getItemByHierarchicalIndex(itemHierarchicalIndex, state.tree) : getItemByHierarchicalIndex(itemHierarchicalIndex, state.filteredFilters)) : {...action.payload.props.item}
      
      // Initialize values array if it doesn't exist
      if (!item.values) {
        item.values = [];
      }
      
      let values = [...item.values];
      const filterText = action.payload.filter.text;
      
      if (action.payload.value) {
        // Add the value if it's not already in the array
        if (!values.includes(filterText)) {
          values.push(filterText);
        }
      } else {
        // Remove the value from the array
        values = values.filter(value => value !== filterText);
      }
      
      // Set the updated values back to the item
      item.values = values;

      // Update the checkbox state in tagAttributeAllowedValues
      const currentFilters = state.tagAttributeAllowedValues.find(e => e.tagId === action.payload.id);
      if (currentFilters) {
        const filterIndex = currentFilters.values.findIndex(filter => filter.text === filterText);
        if (filterIndex !== -1) {
          // Create a new object instead of modifying the existing one
          currentFilters.values = [
            ...currentFilters.values.slice(0, filterIndex),
            { ...currentFilters.values[filterIndex], checked: action.payload.value },
            ...currentFilters.values.slice(filterIndex + 1)
          ];
        }
      }
      
      item.filterApplied = !!item.values?.length

      const indexes = itemHierarchicalIndex != undefined ?  itemHierarchicalIndex.split("_") : [0,0,0]
      const keyStringWithItems = indexes.join("_items_")
      const keys = keyStringWithItems.split("_")
      keys.reduce(
        (nestedTree: any, key: any, index: number) => {
          if (nestedTree && typeof nestedTree === "object" && key in nestedTree) {
            if (index === keys.length - 1) {
              nestedTree[key] = item
            }
            return nestedTree[key]
          } else {
            return undefined
          }
        },
        filtersSearched ? state.filteredFilters[0].items : state.tree[0].items,
      )
      
      // Now update the filter in the filterOptions section to make sure it persists
      const filterIndex = state.filters.filterOptions.filters.findIndex(filter => filter.tagAttributeId === action.payload.id);
      if (values.length > 0) {
        if (filterIndex >= 0) {
          // Update existing filter
          state.filters.filterOptions.filters[filterIndex].values = values;
        } else {
          // Create new filter
          const newFilter: IFilter = {
            tagAttributeId: action.payload.id,
            partOne: { 
              value: '', 
              ignoreCase: true, 
              operationId: 3, 
              operationNameNumeric: 'Contains', 
              operationNameString: 'Contains'
            },
            booleanOperationId: 1,
            partTwo: { 
              value: '', 
              ignoreCase: true, 
              operationId: 4, 
              operationNameNumeric: 'Does not contain', 
              operationNameString: 'Does not contain'
            },
            values
          };
          state.filters.filterOptions.filters.push(newFilter);
        }
      } else if (filterIndex >= 0 && values.length === 0) {
        // Remove filter if no values and no conditions
        const filter = state.filters.filterOptions.filters[filterIndex];
        const hasConditions = filter.partOne?.value || filter.partTwo?.value;
        if (!hasConditions) {
          state.filters.filterOptions.filters.splice(filterIndex, 1);
        } else {
          // Keep the filter but clear the values
          state.filters.filterOptions.filters[filterIndex].values = [];
        }
      }
    },

    firstConditionList: (state, action) => {
      const {
        props: { itemHierarchicalIndex },
        filtersSearched,
      } = action.payload
      const item = !filtersSearched ? getItemByHierarchicalIndex(itemHierarchicalIndex, state.tree) : getItemByHierarchicalIndex(itemHierarchicalIndex, state.filteredFilters)
      item.firstConditionList = action.payload.value
      item.filterApplied = checkFilterActiveStatus(item)
    },

    logicalOperationList: (state, action) => {
      const {
        props: { itemHierarchicalIndex },
        filtersSearched,
      } = action.payload
      const item = !filtersSearched ? getItemByHierarchicalIndex(itemHierarchicalIndex, state.tree) : getItemByHierarchicalIndex(itemHierarchicalIndex, state.filteredFilters)
      item.logicalOperationList = action.payload.value
      item.filterApplied = checkFilterActiveStatus(item)
    },

    secondConditionList: (state, action) => {
      const {
        props: { itemHierarchicalIndex },
        filtersSearched,
      } = action.payload
      const item = !filtersSearched ? getItemByHierarchicalIndex(itemHierarchicalIndex, state.tree) : getItemByHierarchicalIndex(itemHierarchicalIndex, state.filteredFilters)
      item.secondConditionList = action.payload.value
      item.filterApplied = checkFilterActiveStatus(item)
    },

    clearFilter: (state, action) => {
      const {
        props: {
          itemHierarchicalIndex,
          item: { element },
        },
        filtersSearched,
      } = action.payload
      state.tagAttributeAllowedValues = state.tagAttributeAllowedValues.map(tag => {
        // Create a new tag object with values that have checked=false
        return {
          ...tag,
          values: tag.values.map(value => ({
            ...value,
            checked: false
          }))
        };
      });

      const current = itemHierarchicalIndex != "" ? (!filtersSearched ? getNestedValue(state.tree, itemHierarchicalIndex) : getNestedValue(state.filteredFilters, itemHierarchicalIndex)) : {...action.payload.props.item}
      const currentIndex = state.activeFiltersLegend.findIndex((filterLegend: IFilterLegend) => filterLegend.filter === current.name)
      state.activeFiltersLegend.splice(currentIndex, 1)

      current.firstConditionList = element === "filter" ? { id: 3, name: "Contains", defaultPriority: 1 } : { id: 5, name: "Is greater than", defaultPriority: 1 }
      current.firstConditionInput = ""
      current.logicalOperationList = { id: 1, name: "And", defaultPriority: 1 }
      current.secondConditionList = element === "filter" ? { id: 4, name: "Does not contain", defaultPriority: 2 } : { id: 3, name: "Is less than", defaultPriority: 2 }
      current.secondConditionInput = ""
      current.values = []
      current.filterApplied = false
      const { id } = action.payload.props.item
      state.filters.filterOptions.filters.find((filter: IFilter, index: number) => {
        if (filter?.tagAttributeId === id) {
          // need to understand why filter is undefined when refresh page with filters applied
          state.filters.filterOptions.filters.splice(index, 1)
        }
      })
      if (!state.filters.filterOptions.filters.length) {
        state.tree[0].name = "Filters"
      }
    },

    getDropdownData: (state, action) => {
      const options = JSON.parse(JSON.stringify(state.numericOptions))
      getItemByHierarchicalIndex(action.payload.itemHierarchicalIndex, state.tree).options = options
    },

    setSelectedTab: (state, { payload }: PayloadAction<ITab>) => {
      const activeTab = state.tabs.find((tab: ITab) => tab.isSelected)
      if (activeTab) activeTab.isSelected = false

      const selectedTab = state.tabs.find((tab: ITab) => tab.id === payload.id)

      if (selectedTab) {
        selectedTab.isSelected = true
        state.plotId = selectedTab.id
      }

      if (payload.type === "mapView") {
        state.plotId = ""
        return
      }

      // Cross Plot
      if (selectedTab) {
        const xAxis = state.numericOptions.find((option: ICategorizeOption) => option.id === selectedTab.xTagAttributeId)
        const yAxis = state.numericOptions.find((option: ICategorizeOption) => option.id === selectedTab.yTagAttributeId)

        selectedTab.xAxis = xAxis?.name
        selectedTab.yAxis = yAxis?.name
      }

      const filters: IFilterLegend[] = []
      state.filtersTreeView.forEach((filterItem) => {
        filterItem?.items.forEach((node: TreeViewDataItem) => {
          node.items.forEach((item) => {
            if (item.filterApplied) {
              const LegendValueFromConditionInputs = [
                { list: item.firstConditionList, input: item.firstConditionInput },
                { list: item.secondConditionList, input: item.secondConditionInput },
              ].reduce((acc, filterItem) => {
                if (filterItem.input) {
                  acc += `${acc ? ` ${item.logicalOperationList?.name}` : ""} ${filterItem?.list?.name} ${filterItem?.input}`
                }
                return acc
              }, "")
              const finalValues: string[] = []
              if (item.values?.length) {
                finalValues.push(...item.values)
              }
              if (LegendValueFromConditionInputs) {
                finalValues.push(LegendValueFromConditionInputs)
              }

              filters.push({
                filter: item.name,
                values: finalValues,
              })
            }
            item.items.forEach((item: TreeViewDataItem) => {
              if (item.filterApplied) {
                const LegendValueFromConditionInputs = [
                  { list: item.firstConditionList, input: item.firstConditionInput },
                  { list: item.secondConditionList, input: item.secondConditionInput },
                ].reduce((acc, filterItem) => {
                  if (filterItem.input) {
                    acc += `${acc ? ` ${item.logicalOperationList?.name}` : ""} ${filterItem?.list?.name} ${filterItem?.input}`
                  }
                  return acc
                }, "")
                const finalValues: string[] = []
                if (item.values?.length) {
                  finalValues.push(...item.values)
                }
                if (LegendValueFromConditionInputs) {
                  finalValues.push(LegendValueFromConditionInputs)
                }
                filters.push({
                  filter: item.name,
                  values: finalValues,
                })
              }
            })
          })
        })
      })
      state.activeFiltersLegend = filters
    },
    updateTabOrder: (state, action) => {
    const plotIdAndTabOrder = action.payload

    const newTabs = [...state.tabs]

    plotIdAndTabOrder.forEach((item: { plotId: string; order: number }) => {
        const tab = newTabs.find((tab) => tab.id === item.plotId)
        if (tab) {
            tab.order = item.order
        }
    })

    newTabs.sort((a, b) => (a.order !== undefined && b.order !== undefined) ? a.order - b.order : 0)

    state.tabs = newTabs
    },

    changeItemOrder: (state, action: PayloadAction<{itemId: string, parentId: string | null, direction: 'up' | 'down'}>) => {
      const { itemId, parentId, direction } = action.payload;
      
      let itemsArray;
      if (parentId === null) {
        itemsArray = state.tree;
      } else {
        const findParent = (items: TreeViewDataItem[]): TreeViewDataItem | undefined => {
          for (const item of items) {
            if (item.id === parentId) return item;
            if (item.items && item.items.length) {
              const found = findParent(item.items);
              if (found) return found;
            }
          }
          return undefined;
        };
        
        const parentItem = findParent(state.tree);
        if (!parentItem || !parentItem.items) return;
        itemsArray = parentItem.items;
      }
      
      const itemIndex = itemsArray.findIndex(item => item.id === itemId);
      if (itemIndex === -1) return;
      
      let newIndex;
      if (direction === 'up') {
        newIndex = Math.max(0, itemIndex - 1);
      } else {
        newIndex = Math.min(itemsArray.length - 1, itemIndex + 1);
      }
      
      if (newIndex === itemIndex) return;
      
      const [item] = itemsArray.splice(itemIndex, 1);
      itemsArray.splice(newIndex, 0, item);
    },

    saveCurrentWorkspaceViewState: (state) => {
      if (state.workspaceId) {
        const currentViewState = {
          workspaceId: state.workspaceId,
          selectedTabId: state.tabs.find(tab => tab.isSelected)?.id,
          sidebarState: {
            hiddenSidebar: state.hiddenSidebar,
          }
        };
        
        state.workspaceViewStates[state.workspaceId] = {
          ...state.workspaceViewStates[state.workspaceId],
          ...currentViewState
        };
        
        saveWorkspaceViewState(state.workspaceId, currentViewState);
      }
    },

    saveWorkspaceMapViewState: (state, action: PayloadAction<{ workspaceId: string; mapViewState: any }>) => {
      const { workspaceId, mapViewState } = action.payload;
      if (workspaceId) {
        const viewState = {
          workspaceId,
          mapViewState
        };
        
        state.workspaceViewStates[workspaceId] = {
          ...state.workspaceViewStates[workspaceId],
          ...viewState
        };
        
        saveWorkspaceViewState(workspaceId, viewState);
      }
    },

    saveWorkspacePlotViewState: (state, action: PayloadAction<{ workspaceId: string; tabId: string; plotViewState: any }>) => {
      const { workspaceId, tabId, plotViewState } = action.payload;
      if (workspaceId && tabId) {
        if (!state.workspaceViewStates[workspaceId]) {
          state.workspaceViewStates[workspaceId] = { workspaceId };
        }
        if (!state.workspaceViewStates[workspaceId].plotViewStates) {
          state.workspaceViewStates[workspaceId].plotViewStates = {};
        }
        
        state.workspaceViewStates[workspaceId].plotViewStates![tabId] = plotViewState;
        
        saveWorkspaceViewState(workspaceId, state.workspaceViewStates[workspaceId]);
      }
    },

    restoreWorkspaceViewState: (state, action: PayloadAction<string>) => {
      const workspaceId = action.payload;
      const savedViewState = state.workspaceViewStates[workspaceId];
      
      if (savedViewState) {
        // Restore sidebar state
        if (savedViewState.sidebarState) {
          state.hiddenSidebar = savedViewState.sidebarState.hiddenSidebar;
        }
        
        // The selected tab and map view state will be handled by their respective components
        // as they need to trigger their own state updates
      }
    },

    restoreLayerOrder: (state, action: PayloadAction<Array<{id: string, name: string, element: string}>>) => {
      const layerOrder = action.payload;
      
      // Check if tree[0] exists and has items
      if (state.tree[0] && state.tree[0].items && layerOrder.length > 0) {
        // Create a map of the current items by id for quick lookup
        const itemsMap = new Map(state.tree[0].items.map(item => [item.id, item]));
        
        // Reorder the items based on the saved order
        const reorderedItems = layerOrder
          .map(orderItem => itemsMap.get(orderItem.id))
          .filter(item => item !== undefined) as TreeViewDataItem[];
        
        // Add any new items that weren't in the saved order at the end
        const savedIds = new Set(layerOrder.map(item => item.id));
        const newItems = state.tree[0].items.filter(item => !savedIds.has(item.id));
        
        // Update the tree with the reordered items
        state.tree[0].items = [...reorderedItems, ...newItems];
      }
    },
  },
  extraReducers: {
    [saveMapOptionsThunk.rejected.type]: (state, action: PayloadAction<string>) => {
      state.error = action.payload
    },

    [T_GetUserActions.fulfilled.type]: (state, action: PayloadAction<IUserActions>) => {
      state.userActions = action.payload
    },

    [T_GetUserActions.rejected.type]: (state) => {
      state.isLoading = false
    },

    [getAllWorkspacesThunk.pending.type]: (state) => {
      state.isLoading = true
    },

    [getAllWorkspacesThunk.fulfilled.type]: (state, action: PayloadAction<IWorkspaceItem[]>) => {
      state.workspaces = action.payload
      state.isLoading = false
      state.isWorkspaceListFetched = true
      state.error = null
    },

    [getAllWorkspacesThunk.rejected.type]: (state, action: PayloadAction<string>) => {
      state.isLoading = false
      state.error = action.payload
      state.isWorkspaceListFetched = true
    },

    [T_addWorkspace.pending.type]: (state) => {
      state.workspaceListLoading = true
    },
    [T_addWorkspace.fulfilled.type]: (state) => {
      state.workspaceListLoading = false
    },

    [T_editWorkspace.pending.type]: (state) => {
      state.workspaceListLoading = true
    },

    [T_editWorkspace.fulfilled.type]: (state, action) => {
      const workspace = state.workspaces.find((workspace: IWorkspaceItem) => workspace.id === action.payload.id)
      if (workspace) workspace.title = action.payload.title
      state.workspaceListLoading = false
    },

    [T_editWorkspaceFilters.rejected.type]: (state) => {
      state.workspaceListLoading = false
      state.workspaceEdited = false
    },

    [T_editWorkspaceFilters.pending.type]: (state) => {
      state.workspaceListLoading = true
    },

    [T_editWorkspaceFilters.fulfilled.type]: (state, action: PayloadAction<{ categorizeOptions?: ICategorizeOptions }>) => {
      state.workspaceListLoading = false
      state.workspaceEdited = true

      // Update categorizeOptions with the response from server
      if (action.payload?.categorizeOptions) {
        state.filters.categorizeOptions = {
          ...state.filters.categorizeOptions,
          ...action.payload.categorizeOptions
        };
      }
    },

    [T_editWorkspaceFilterOptions.pending.type]: (state) => {
      state.workspaceListLoading = true
    },

    [T_editWorkspaceFilterOptions.fulfilled.type]: (state) => {
      state.workspaceListLoading = false
      state.workspaceEdited = true
    },

    [T_editWorkspaceFilterOptions.rejected.type]: (state) => {
      state.workspaceListLoading = false
      state.workspaceEdited = false
    },

    [getWorkspaceByIdThunk.pending.type]: (state) => {
      state.isLoading = true
    },

    [getWorkspaceByIdThunk.fulfilled.type]: (
      state,
      action: PayloadAction<{ tabs: ITab[]; workspace: IWorkspace; allTags: (ITagAttribute & IOptions)[]; projectOptions: IProjectViewOption[]; tabId?: string }>,
    ) => {
      const { id, plots, filterOptions, categorizeOptions } = action.payload.workspace
      state.filters.categorizeOptions = categorizeOptions || { tagAttributeId: null, enabled: false, logScale: false, applyToUserData: false, mode: 'basic' }
      state.workspaceId = id
      state.tabs = action.payload.tabs
      state.plots = action.payload.workspace.plots
      state.isLoading = false
      state.error = null
      saveWorkSpaceToLS(id)
      state.mapOptions = action.payload.workspace.mapViewOptions
      
      // Update the workspace in the workspaces array with the processed workspace (including accessLevel and isOwner)
      const workspaceIndex = state.workspaces.findIndex((ws: IWorkspaceItem) => ws.id === id);
      if (workspaceIndex !== -1) {
        state.workspaces[workspaceIndex] = {
          ...state.workspaces[workspaceIndex],
          isOwner: action.payload.workspace.isOwner ?? state.workspaces[workspaceIndex].isOwner,
          accessLevel: action.payload.workspace.accessLevel
        };
      }
      const selectedTab = action.payload.tabs.find((tab: ITab) => tab.isSelected)
      const selectedPlot = plots.find((plot: IPlot) => plot.id === selectedTab?.id) || plots[0] // in the future, it will be removed

      if (selectedPlot && selectedPlot.type === 1) {
        state.filters = { ...state.filters, ...selectedPlot }
        state.filters.id = selectedPlot.id
        state.filters.title = selectedPlot.title
        state.filters.xTagAttributeId = selectedPlot.xTagAttributeId
        state.filters.yTagAttributeId = selectedPlot.yTagAttributeId
        
        // Ensure categorizeOptions is always initialized
        if (!state.filters.categorizeOptions) {
          state.filters.categorizeOptions = { tagAttributeId: null, enabled: false, logScale: false, applyToUserData: false, mode: 'basic' };
        }
      }

      if (filterOptions?.filters?.length) {
        state.filters.filterOptions = { ...state.filters.filterOptions, ...filterOptions }
      } else {
        state.filters.filterOptions = {
          ...state.filters.filterOptions,
          filters: [],
        }
      }

      const filtersTree = state.tree.find((node: TreeViewDataItem) => node.name === "Filters")
      const filters: IFilterLegend[] = []
      filtersTree?.items.forEach((node: TreeViewDataItem) => {
        node.items.forEach((item: TreeViewDataItem) => {
          if (item.filterApplied) {
            const LegendValueFromConditionInputs = [
              { list: item.firstConditionList, input: item.firstConditionInput },
              { list: item.secondConditionList, input: item.secondConditionInput },
            ].reduce((acc, filterItem) => {
              if (filterItem.input) {
                acc += `${acc ? ` ${item.logicalOperationList?.name}` : ""} ${filterItem?.list?.name} ${filterItem?.input}`
              }
              return acc
            }, "")
            const finalValues: string[] = []
            if (item.values?.length) {
              finalValues.push(...item.values)
            }
            if (LegendValueFromConditionInputs) {
              finalValues.push(LegendValueFromConditionInputs)
            }

            filters.push({
              filter: item.name,
              values: finalValues,
            })
          }
        })
      })
      state.activeFiltersLegend = filters
    },

    [getWorkspaceByIdThunk.rejected.type]: (state, action: PayloadAction<string>) => {
      state.isLoading = false
      state.error = action.payload
    },

    [getSideBarFiltersThunk.pending.type]: (state) => {
      state.isLoading = true
    },

    [getSideBarFiltersThunk.fulfilled.type]: (
      state,
      action: PayloadAction<{
        filtersTree: TreeViewDataItem[]
        numericOptions: ICategorizeOption[]
        stringOptions: ICategorizeOption[]
      }>,
    ) => {
      state.isLoading = false
      state.error = null
      const { filtersTree, numericOptions, stringOptions } = action.payload
      state.filtersTreeView[0].items = filtersTree
      state.numericOptions = numericOptions
      state.stringOptions = stringOptions
      state.categoriseOptions = [...stringOptions, ...numericOptions].sort((a, b) => {
        if (a.name < b.name) {
          return -1
        }
        if (a.name > b.name) {
          return 1
        }
        return 0
      })

      state.filtersTreeView[0].items = state.filtersTreeView[0].items.map((item: TreeViewDataItem) => {
        item.items.map((subItem: TreeViewDataItem) => {
          if (subItem.values) {
            const filter: IFilter | undefined = state.filters.filterOptions.filters.find((filter: IFilter) => filter.tagAttributeId === subItem.id)
            if (filter) {
              subItem.values = filter.values
              subItem.filterApplied = true
              subItem.firstConditionInput = filter.partOne?.value
              subItem.secondConditionInput = filter.partTwo?.value
              // todo fix it with typings
              subItem.firstConditionList!.id = filter.partOne!.operationId as number
              subItem.firstConditionList!.name = (subItem.type === "numeric" ? filter.partOne!.operationNameNumeric : filter.partOne!.operationNameString) as string
              subItem.secondConditionList!.id = filter.partTwo!.operationId as number
              subItem.secondConditionList!.name = (subItem.type === "numeric" ? filter.partTwo!.operationNameNumeric : filter.partTwo!.operationNameString) as string
              subItem.logicalOperationList!.id = filter.booleanOperationId as number
            } else {
              subItem.filterApplied = false
            }
            return subItem
          } else {
            return subItem.items.map((nestedSubitem) => {
              const filter: IFilter | undefined = state.filters.filterOptions.filters.find((filter: IFilter) => filter.tagAttributeId === nestedSubitem.id)
              if (filter) {
                nestedSubitem.values = filter.values
                nestedSubitem.filterApplied = true
                nestedSubitem.firstConditionInput = filter.partOne?.value
                nestedSubitem.secondConditionInput = filter.partTwo?.value
                // todo fix it with typings
                nestedSubitem.firstConditionList!.id = filter.partOne!.operationId as number
                nestedSubitem.firstConditionList!.name = (nestedSubitem.type === "numeric" ? filter.partOne!.operationNameNumeric : filter.partOne!.operationNameString) as string
                nestedSubitem.secondConditionList!.id = filter.partTwo!.operationId as number
                nestedSubitem.secondConditionList!.name = (nestedSubitem.type === "numeric" ? filter.partTwo!.operationNameNumeric : filter.partTwo!.operationNameString) as string
                nestedSubitem.logicalOperationList!.id = filter.booleanOperationId as number
              } else {
                nestedSubitem.filterApplied = false
              }
              return nestedSubitem
            })
          }
        })

        return item
      })

      // state.filtersTreeView[0].items = state.filtersTreeView[0].items.map((item: TreeViewDataItem) => {
      //     item.items.map((subItem: TreeViewDataItem) => {
      //         const filter: IFilter | undefined = state.filters.filterOptions.filters.find((filter: IFilter) => filter.tagAttributeId === subItem.id);
      //         if (filter) {
      //             subItem.values = filter.values

      //             subItem.filterApplied = true;
      //             subItem.firstConditionInput = filter.partOne?.value;
      //             subItem.secondConditionInput = filter.partTwo?.value;
      //             // todo fix it with typings
      //             subItem.firstConditionList!.id = filter.partOne!.operationId as number;
      //             subItem.secondConditionList!.id = filter.partTwo!.operationId as number;
      //             subItem.logicalOperationList!.id = filter.booleanOperationId as number;
      //         } else {

      //             subItem.filterApplied = false;
      //         }
      //         return subItem;
      //     });

      //     return item;
      // });

      const filtersTreeClone = state.filtersTreeView.find((node: TreeViewDataItem) => node.name === "Filters")
      const filters: IFilterLegend[] = []
      state.filtersTreeView.forEach((filterItem) => {
        filterItem?.items.forEach((node: TreeViewDataItem) => {
          if (node.element === "EntryType") {
            node.items.forEach((item: TreeViewDataItem) => {
              if (item.filterApplied) {
                const LegendValueFromConditionInputs = [
                  { list: item.firstConditionList, input: item.firstConditionInput },
                  { list: item.secondConditionList, input: item.secondConditionInput },
                ].reduce((acc, filterItem) => {
                  if (filterItem.input) {
                    acc += `${acc ? ` ${item.logicalOperationList?.name}` : ""} ${filterItem?.list?.name} ${filterItem?.input}`
                  }
                  return acc
                }, "")
                const finalValues: string[] = []
                if (item.values?.length) {
                  finalValues.push(...item.values)
                }
                if (LegendValueFromConditionInputs) {
                  finalValues.push(LegendValueFromConditionInputs)
                }

                filters.push({
                  filter: item.name,
                  values: finalValues,
                })
              }
            })
          } else {
            node.items.forEach((item) => {
              if (item.filterApplied) {
                const LegendValueFromConditionInputs = [
                  { list: item.firstConditionList, input: item.firstConditionInput },
                  { list: item.secondConditionList, input: item.secondConditionInput },
                ].reduce((acc, filterItem) => {
                  if (filterItem.input) {
                    acc += `${acc ? ` ${item.logicalOperationList?.name}` : ""} ${filterItem?.list?.name} ${filterItem?.input}`
                  }
                  return acc
                }, "")
                const finalValues: string[] = []
                if (item.values?.length) {
                  finalValues.push(...item.values)
                }
                if (LegendValueFromConditionInputs) {
                  finalValues.push(LegendValueFromConditionInputs)
                }

                filters.push({
                  filter: item.name,
                  values: finalValues,
                })
              }
              item.items.forEach((item: TreeViewDataItem) => {
                if (item.filterApplied) {
                  const LegendValueFromConditionInputs = [
                    { list: item.firstConditionList, input: item.firstConditionInput },
                    { list: item.secondConditionList, input: item.secondConditionInput },
                  ].reduce((acc, filterItem) => {
                    if (filterItem.input) {
                      acc += `${acc ? ` ${item.logicalOperationList?.name}` : ""} ${filterItem?.list?.name} ${filterItem?.input}`
                    }
                    return acc
                  }, "")
                  const finalValues: string[] = []
                  if (item.values?.length) {
                    finalValues.push(...item.values)
                  }
                  if (LegendValueFromConditionInputs) {
                    finalValues.push(LegendValueFromConditionInputs)
                  }

                  filters.push({
                    filter: item.name,
                    values: finalValues,
                  })
                }
              })
            })
          }
        })
      })
      state.activeFiltersLegend = filters
      state.tree[0] = state.filtersTreeView[0]
    },

    [getSideBarFiltersThunk.rejected.type]: (state, action: PayloadAction<string>) => {
      state.isLoading = false
      state.error = action.payload
    },

    [GetTagAttributeAllowedValuesThunk.pending.type]: (state) => {
      state.loadAllowedValues = true
    },

    [GetTagAttributeAllowedValuesThunk.fulfilled.type]: (state, action) => {
      state.loadAllowedValues = false
      state.error = null
      const { tagId, values } = action.payload
      // Find any existing filter for this tag
      const existingFilter = state.filters.filterOptions.filters.find(filter => filter.tagAttributeId === tagId);
      
      // todo remove tagAttributeAllowedValues from state and move it on treeview
      state.tagAttributeAllowedValues = [
        {
          tagId,
          values: values.map((text: string) => {
            // Check if this value exists in the current filter
            const isChecked = existingFilter ? (existingFilter.values || []).includes(text) : false;
            return {
              text,
              checked: isChecked
            }
          }),
        },
      ]
      const itemHierarchicalIndex = action.meta.arg.props.itemHierarchicalIndex

      const item = getItemByHierarchicalIndex(itemHierarchicalIndex, state.tree)
      if (item) {
        item.values = state.tagAttributeAllowedValues?.[0]?.values?.filter((value) => value?.checked)?.map((value) => value?.text)
      }
    },

    [GetTagAttributeAllowedValuesThunk.rejected.type]: (state, action: PayloadAction<string>) => {
      state.loadAllowedValues = false
      state.error = action.payload
    },

    [GetTagAttributeInfoThunk.pending.type]: (state) => {
      // Optional: Add loading state if needed
    },

    [GetTagAttributeInfoThunk.fulfilled.type]: (state, action) => {
      // Store the tag info - just the tagId for now as requested
      const { tagId } = action.payload;
    },
    [GetDropdownAllTagAttributeAllowedValuesThunk.pending.type]: (state) => {
      state.loadAllowedValues = true;
    },

    [GetDropdownAllTagAttributeAllowedValuesThunk.fulfilled.type]: (state, action) => {
      const { tagId, values, mode, relevantFilters } = action.payload
      
      // Find existing filter from the relevant filters based on mode
      // relevantFilters contains either explore or analogue filters depending on mode
      const existingFilter = relevantFilters?.find((filter: any) => filter.tagAttributeId === tagId);
      // todo remove tagAttributeAllowedValues from state and move it on treeview
      state.dropdownAllTagAttributeValues = [
        {
          tagId,
          values: values.map((text: string) => {
            // Check if this value exists in the current filter (explore or analogue based on mode)
            const isChecked = existingFilter ? (existingFilter.values || []).includes(text) : false;
            return {
              text,
              checked: isChecked
            }
          }),
        },
      ];

      state.loadAllowedValues = false;
    },

    [GetDropdownAllTagAttributeAllowedValuesThunk.rejected.type]: (state) => {
      state.loadAllowedValues = false;
    },

    [savePlotOptionsThunk.pending.type]: (state) => {
      state.error = null
    },

    [savePlotOptionsThunk.fulfilled.type]: (state) => {
      state.isLoading = false
      state.error = null
    },

    [savePlotOptionsThunk.rejected.type]: (state, action: PayloadAction<string>) => {
      state.isLoading = false
      state.error = action.payload
    },

    [T_getProjectView.pending.type]: (state) => {
      state.isLoading = true
    },

    [T_getProjectView.fulfilled.type]: (state, action) => {
      const selectedTab = state.tabs.find((item) => item.isSelected)
      if (selectedTab && action.payload.tags) {
        const currentPlot = state.plots.findIndex((plot: IPlot) => plot.id === selectedTab.id)
        const normalized = normalizeByKey(action.payload.tags, "key")
        if (currentPlot > -1) {
          state.plots[currentPlot] = { ...state.plots[currentPlot], identifierFieldId: action.payload.id, ...normalized }
        }
        const projectViewOptionsItems = state.projectViewTreeView.items.find((item) => item.element === "projectViewoption")
        if (projectViewOptionsItems) {
          const selected = projectViewOptionsItems.options?.find((item) => item.parentId === action.payload.id)
          const selectedItemFromFull = state.projectViewOptions
          projectViewOptionsItems.selected = selected || (selectedItemFromFull as unknown as IOptions)
          const tabIndex = state.tabs.findIndex((item) => item.id === action.payload.plotId)
          if (tabIndex > -1) {
            state.tabs[tabIndex].id = action.payload.plotId
            if (selected) state.tabs[tabIndex].parentId = selected?.parentId
          }
        }
      }
      state.filters = { ...state.filters, ...action.payload }
      
      // Ensure categorizeOptions is always initialized
      if (!state.filters.categorizeOptions) {
        state.filters.categorizeOptions = { tagAttributeId: null, enabled: false, logScale: false, applyToUserData: false, mode: 'basic' };
      }
      
      state.isLoading = false
      state.error = null
    },

    [T_getProjectView.rejected.type]: (state, action: PayloadAction<string>) => {
      state.isLoading = false
      state.error = action.payload
    },

    [setSharedUsersThunk.pending.type]: (state) => {
      state.isLoading = true
    },

    [setSharedUsersThunk.fulfilled.type]: (state, action) => {
      const currentWorkspaceIndex = state.workspaces.findIndex((workspace: IWorkspaceItem) => workspace.id == action.payload.id)
      state.workspaces[currentWorkspaceIndex] = action.payload
      state.isLoading = false
    },

    [setSharedUsersThunk.rejected.type]: (state, action: PayloadAction<string>) => {
      state.isLoading = false
      state.error = action.payload
    },
    [T_getStyleSettings.pending.type]: (state) => {
      state.error = null
    },
    [T_getStyleSettings.rejected.type]: (state, action: PayloadAction<string>) => {
      state.error = action.payload
    },
    [T_getStyleSettings.fulfilled.type]: (state, action: PayloadAction<IStyleSettings>) => {
      state.error = null
      state.styleSettings = action.payload
    },

    [T_changeStyleSettings.pending.type]: (state) => {
      state.isLoading = true
      state.error = null
    },
    [T_changeStyleSettings.rejected.type]: (state, action: PayloadAction<string>) => {
      state.isLoading = false
      state.error = action.payload
    },
    [T_changeStyleSettings.fulfilled.type]: (state, action: PayloadAction<IStyleSettings>) => {
      state.isLoading = false
      state.error = null
      // state.styleSettings = action.payload
    },

    [T_getAxisStyleSettings.pending.type]: (state) => {
      state.isLoading = true
      state.error = null
    },
    [T_getAxisStyleSettings.rejected.type]: (state, action: PayloadAction<string>) => {
      state.isLoading = false
      state.error = action.payload
    },
    [T_getAxisStyleSettings.fulfilled.type]: (state, action: PayloadAction<IAxisStyleSettings>) => {
      state.isLoading = false
      state.error = null
      state.axisStyleSettings = action.payload
    },
  },
})

export default workspaceSlice.reducer;


const selectSelectedTabUncached = (state: RootState) => state.workspaceSlice.tabs.find((tab: ITab) => tab.isSelected);
// need to be sure that caching works
export const selectSelectedTab = createSelector(
    selectSelectedTabUncached,
    (state: RootState) => state,
    (tab) => tab
);